export function createChildLinkFlow({
  userId, api, storage, notify, refresh, onChange, timeoutMs = 15000,
}) {
  let active = true;
  let generation = 0;
  let state = { pending: null, busy: null, loading: false, dismissed: false, error: null };
  const requests = new Set();
  const hidden = new Set();
  const key = id => "proximity:child-link-dismissed:" + userId + ":" + id;
  const update = changes => {
    state = { ...state, ...changes };
    if (active) onChange(state);
  };
  const isHidden = id => {
    try { return hidden.has(id) || storage.getItem(key(id)) === "1"; }
    catch { return hidden.has(id); }
  };
  const hide = id => {
    hidden.add(id);
    try { storage.setItem(key(id), "1"); } catch { /* Session fallback is in memory. */ }
  };
  const cancel = () => {
    generation += 1;
    for (const controller of requests) controller.abort();
    requests.clear();
  };
  const errorText = (error, fallback) => typeof error?.response?.data?.detail === "string"
    ? error.response.data.detail : error?.code === "LINK_TIMEOUT"
      ? "Request timed out. You can retry or close this request." : fallback;
  const request = async (method, path) => {
    const controller = new AbortController();
    requests.add(controller);
    let timer, abort;
    const interrupted = new Promise((_, reject) => {
      abort = () => reject(new Error("Cancelled"));
      controller.signal.addEventListener("abort", abort, { once: true });
      timer = setTimeout(() => {
        reject(Object.assign(new Error("Timed out"), { code: "LINK_TIMEOUT" }));
        controller.abort();
      }, timeoutMs);
    });
    try {
      const config = { signal: controller.signal, timeout: timeoutMs };
      return await Promise.race([
        method === "get" ? api.get(path, config) : api.post(path, undefined, config),
        interrupted,
      ]);
    } finally {
      clearTimeout(timer);
      controller.signal.removeEventListener("abort", abort);
      requests.delete(controller);
    }
  };
  const load = async () => {
    if (!active || !userId || state.busy || state.loading) return;
    const epoch = generation;
    update({ loading: true });
    try {
      const r = await request("get", "/child/pending-link");
      if (!active || epoch !== generation) return;
      if (r.data === null) { update({ pending: null, dismissed: false, error: null }); return; }
      if (!r.data || typeof r.data.id !== "string" || !r.data.id ||
          r.data.status !== "pending_link" || r.data.child_confirmed !== false) {
        throw new Error("Invalid pending-link response");
      }
      update({ pending: r.data, dismissed: isHidden(r.data.id), error: null });
    } catch (error) {
      if (active && epoch === generation) {
        update({ error: errorText(error, "Unable to check this link request. Please retry.") });
      }
    } finally {
      if (active && epoch === generation) update({ loading: false });
    }
  };
  const dismiss = () => {
    if (!active || !state.pending) return;
    hide(state.pending.id);
    cancel();
    update({ dismissed: true, busy: null, loading: false, error: null });
  };
  const review = async () => {
    if (!active || !state.pending) return;
    hidden.delete(state.pending.id);
    try { storage.removeItem(key(state.pending.id)); } catch { /* In-memory fallback. */ }
    update({ dismissed: false, error: null });
    await load();
  };
  const act = async action => {
    if (!active || !state.pending || state.dismissed || state.busy || state.loading) return;
    const link = state.pending;
    cancel(); // invalidate any poll results before changing server state
    const epoch = generation;
    update({ busy: action, error: null });
    try {
      const r = await request("post", "/child/" + action + "-link/" + encodeURIComponent(link.id));
      if (!active || epoch !== generation) return;
      if (action === "confirm") {
        if (r.data?.id !== link.id ||
            !(r.data.status === "linked" ||
              (r.data.status === "pending_link" && r.data.child_confirmed === true))) {
          throw new Error("Confirmation was not acknowledged");
        }
        if (r.data.status === "linked") notify.success("Child account connection confirmed.");
        else notify.message("Your confirmation was saved. Waiting for the parent to confirm.");
      } else {
        if (r.data?.ok !== true) throw new Error("Decline was not acknowledged");
        notify.message("Invitation declined");
      }
      hide(link.id); // a stale poll must not redisplay the resolved request
      update({ pending: null, dismissed: false });
      if (action === "confirm") {
        // Refresh cannot hold the popup open after the server has confirmed.
        Promise.resolve().then(() => active ? refresh() : undefined).catch(() => {
          if (active) notify.error("Confirmation saved, but account details could not refresh. Reopen the app.");
        });
      }
    } catch (error) {
      if (active && epoch === generation) {
        const text = errorText(error, action === "confirm"
          ? "Could not confirm. Please retry or close this request."
          : "Could not decline. Please retry or close this request.");
        update({ error: text });
        notify.error(text);
      }
    } finally {
      if (active && epoch === generation) update({ busy: null });
    }
  };
  return {
    load, dismiss, review, confirm: () => act("confirm"), decline: () => act("decline"),
    dispose: () => { active = false; cancel(); },
  };
}
